import React, { useState } from 'react';
import { Box, Chip, Button, Grid, TextField, Typography, MenuItem } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TaskCreationSchema } from '../../config/forms/employeeTaskFormConfig';
import ApiClient from '../../apis/Api-client';
import { END_POINT } from '../../utils/End-points';
import { successToster, errorToster } from '../../shared/toster/Toster';
import { Helper } from '../../utils/Helpers';
import { PRIORITY_OPTIONS } from '../../utils/Constants';

const TaskCreationForm = ({ handleClose, employeeList = [] }) => {
  const helper = new Helper();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch
  } = useForm({
    resolver: yupResolver(TaskCreationSchema),
    defaultValues: {
      assigned_to_name: [],
      task_description: '',
      due_date: '',
      due_time: '',
      priority: '',
    },
    mode: 'onBlur'
  });

  const { api } = ApiClient();
  const [loading, setLoading] = useState(false);

  const now = new Date();
  const todayDate = now.toISOString().split('T')[0];
  const currentTime = now.toTimeString().slice(0, 5);

  const selectedDate = watch('due_date');
  const isToday = selectedDate === todayDate;

  // Standardized responsive styles using MUI breakpoints
  const inputStyle = {
    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
  };

  const labelStyle = {
    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
    color: "#6F6F6F"
  };

  const commonProps = {
    fullWidth: true,
    size: "small",
    InputLabelProps: { sx: labelStyle },
    InputProps: { sx: inputStyle }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const finalDate = helper.combineDateAndTime(data.due_date, data.due_time);
      const assignedTimeStr = helper.getCurrentAssignedTimeStr();

      const payload = {
        task_description: data.task_description,
        due_at: finalDate.toISOString(),
        priority: data.priority,
        assigned_to: data.assigned_to_name,
        assigned_at: assignedTimeStr
      };

      const response = await api.post(END_POINT.EMPLOYEE.CREATE_EMPLOYEE_TASK, payload);

      if (response?.data?.status || response?.status === 200) {
        successToster("Task Created Successfully!");
        reset();
        handleClose();
      } else {
        errorToster("Failed to create task");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      errorToster(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 2, sm: 3, md: 3 } }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" } }}>
        Create New Task
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="assigned_to_name"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <TextField
                {...field}
                {...commonProps}
                select
                label="Assign Employee"
                error={!!errors.assigned_to_name}
                helperText={errors.assigned_to_name?.message}
                SelectProps={{
                  multiple: true,
                  renderValue: (selectedIds) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedIds.map((id) => (
                        <Chip key={id} label={helper.getEmployeeNameById(id, employeeList)} size="small" />
                      ))}
                    </Box>
                  ),
                  onChange: (event) => {
                    const { value } = event.target;
                    const val = typeof value === 'string' ? value.split(',') : value;
                    if (val.length <= 3) field.onChange(val);
                  }
                }}
              >
                {employeeList.length > 0 ? (
                  employeeList.map((emp) => (
                    <MenuItem key={emp.employee_id} value={emp.employee_id} sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" } }}>
                      {emp.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" } }}>No Employees Found</MenuItem>
                )}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            {...commonProps}
            label="Task Description"
            multiline
            rows={3}
            error={!!errors.task_description}
            helperText={errors.task_description?.message}
            {...register("task_description")}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            {...commonProps}
            label="Due Date"
            type="date"
            inputProps={{ min: todayDate }}
            InputLabelProps={{ shrink: true, sx: labelStyle }}
            error={!!errors.due_date}
            helperText={errors.due_date?.message}
            {...register("due_date")}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            {...commonProps}
            label="Due Time"
            type="time"
            inputProps={{
              min: isToday ? currentTime : undefined
            }}
            InputLabelProps={{ shrink: true, sx: labelStyle }}
            error={!!errors.due_time}
            helperText={errors.due_time?.message}
            {...register("due_time")}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            {...commonProps}
            select
            label="Priority"
            error={!!errors.priority}
            helperText={errors.priority?.message}
            {...register("priority")}
          >
            {PRIORITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" } }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleClose}
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                py: 1
              }}
            >
              CANCEL
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              fullWidth
              type="submit"
              disabled={loading}
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                py: 1
              }}
            >
              {loading ? "CREATING..." : "CREATE TASK"}
            </Button>
          </Grid>
        </Grid>

      </Grid>
    </Box>
  );
};

export default TaskCreationForm;